import { Component, Vue } from 'vue-property-decorator';
import {
  AgencyType,
  FivGoalsFilter,
  IAchievementMap,
  IAgency,
  IProduct,
  IRole,
  IUser,
  UserRoleType,
  UserStatus,
} from 'admiral-core';
import { Util } from '@/utils/Util';
import { AuthAction } from '@/store/AuthModule';

@Component
export class BaseMixin extends Vue {
  get permissions() {
    return this.$store.state.UserModule.user
      ? this.$store.state.UserModule.user.permissions || new Permissions()
      : new Permissions();
  }

  get achievements() {
    return this.user.achievementMap || {};
  }

  hasAchievement(key: keyof IAchievementMap) {
    return this.achievements[key];
  }

  get fivGoalsFilter(): FivGoalsFilter {
    return this.$store.getters[`PreferencesModule/getFivGoalsFilter`] as FivGoalsFilter;
  }
  get noValuesYet() {
    return this.fivGoalsFilter.year === 2023;
  }

  get year() {
    return (this.fivGoalsFilter.year || 2023) === new Date().getFullYear()
      ? ''
      : this.fivGoalsFilter.year || '';
  }

  get user(): IUser {
    return this.$store.state.AuthModule.user;
  }

  get agency(): IAgency {
    return this.$store.state.DataModule.agency;
  }

  get isAuthenticated(): boolean {
    return !!this.user;
  }

  get products(): IProduct[] {
    return this.$store.state.DataModule.products;
  }

  get userStatuses(): UserStatus[] {
    return [UserStatus.ACTIVE, UserStatus.DEACTIVATED];
  }
  get roles(): IRole[] {
    return this.$store.state.DataModule.roles;
  }

  get users(): IUser[] {
    return this.$store.state.DataModule.users;
  }

  get isAdmin(): boolean {
    return (
      !!this.user &&
      (this.user.role.type === UserRoleType.ADMIN ||
        this.user.role.type === UserRoleType.SUPER_ADMIN)
    );
  }

  get isSuperAdmin(): boolean {
    return !!this.user && this.user.role.type === UserRoleType.SUPER_ADMIN;
  }

  hasPermission(roleType: UserRoleType): boolean {
    if (!this.user) {
      return false;
    }
    if (this.user.role.type >= roleType) {
      return true;
    }

    return false;
  }

  get hasAdminPermissions() {
    return this.hasPermission(UserRoleType.ADMIN);
  }

  get hasManagerPermissions() {
    return this.hasPermission(UserRoleType.MANAGER);
  }

  get hasSuperAdminPermissions() {
    return this.hasPermission(UserRoleType.SUPER_ADMIN);
  }

  get hasEmployeePermissions() {
    return this.hasPermission(UserRoleType.EMPLOYEE);
  }

  get automaticLogoutTime() {
    return this.$store.getters[`AuthModule/getLastApiCall`];
  }

  get isBlurred() {
    return this.$store.getters['PreferencesModule/isBlurred'];
  }

  get isAllianzAgency() {
    return this.agency.type === AgencyType.ALLIANZ;
  }

  getProfilePictureUrl(user: IUser) {
    return Util.getProfilePictureUrl(user);
  }

  get isDarkMode(): boolean {
    return this.$store.getters['PreferencesModule/isDarkMode'];
  }
  getInitials(user: IUser) {
    return Util.userInitials(user);
  }

  getAgencyInitials(agency: IAgency) {
    return ''; //Util.agencyInitials(agency);
  }

  get hasBetaAccess(): boolean {
    return (
      ['admiral', 'demo'].includes(this.agency.identifier) ||
      process.env.VUE_APP_NODE_ENV === 'development'
    );
  }

  async handleLogout(timeout = false) {
    try {
      this.$Progress.start();
      this.$cookies.remove('jwtPayload');
      await this.$store.dispatch(`AuthModule/${AuthAction.Logout}`);
      const route: any = {
        name: 'logout',
      };

      if (timeout) {
        route.params.timeout = 'true';
      }

      await this.$router.push(route);

      this.$Progress.finish();
    } catch (e) {
      this.$Progress.fail();
    }
  }

  async fetchDataAfterLogin() {
    await this.$store.dispatch('DataModule/fetchAgency');

    await this.$store.dispatch('DataModule/fetchProducts', {
      agency: this.user.agency,
    });

    await this.fetchUsers();

    await this.$store.dispatch('DataModule/fetchRoles');

    await this.$store.dispatch('DataModule/fetchTags', {
      agency: this.user.agency,
    });

    if (this.user.role.type === UserRoleType.SUPER_ADMIN) {
      await this.$store.dispatch('PreferencesModule/blur', true);
    }
    await this.$store.dispatch('PreferencesModule/resetPolicyFilter');
  }

  async fetchUsers() {
    await this.$store.dispatch('DataModule/fetchUsers', {
      agency: this.user.agency,
      status: [UserStatus.ACTIVE, UserStatus.PENDING],
    });
  }

  get isMobile() {
    return Util.isMobile();
  }
}
